import React, { useState, useEffect } from 'react';
import { platformSettingsApi, hospitalSettingsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Gear, Envelope, CreditCard, ShieldCheck, TestTube,
  CheckCircle, XCircle, Eye, EyeSlash, PaperPlaneTilt, Lightning,
  ChatDots, BellRinging, Clock, Buildings,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

// ================= SUPER ADMIN SETTINGS =================
const SuperAdminSettings = () => {
  const [activeTab, setActiveTab] = useState('smtp');
  const [loading, setLoading] = useState(true);
  const [smtp, setSmtp] = useState({ smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_user: '', smtp_password: '', sender_email: '', sender_name: "Care'O HCMS", configured: false, env_configured: false });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [pg, setPg] = useState({ razorpay_key_id: '', razorpay_key_secret: '', razorpay_configured: false, razorpay_env_configured: false, payu_key: '', payu_salt: '', payu_mode: 'test', payu_configured: false, payu_env_configured: false });
  const [pgSaving, setPgSaving] = useState(false);
  const [rzTesting, setRzTesting] = useState(false);
  const [puTesting, setPuTesting] = useState(false);
  const [showRzSecret, setShowRzSecret] = useState(false);
  const [showPuSalt, setShowPuSalt] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await platformSettingsApi.get();
      setSmtp(s => ({ ...s, ...res.data.smtp }));
      setPg(p => ({ ...p, ...res.data.payment_gateways }));
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const saveSmtp = async () => {
    if (!smtp.smtp_user) { toast.error('SMTP User is required'); return; }
    setSmtpSaving(true);
    try {
      await platformSettingsApi.updateSmtp({ smtp_host: smtp.smtp_host, smtp_port: parseInt(smtp.smtp_port), smtp_user: smtp.smtp_user, smtp_password: smtp.smtp_password, sender_email: smtp.sender_email || smtp.smtp_user, sender_name: smtp.sender_name });
      toast.success('SMTP settings saved');
      loadSettings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSmtpSaving(false); }
  };

  const testSmtp = async () => {
    if (!testEmail) { toast.error('Enter test email'); return; }
    setSmtpTesting(true);
    try { const r = await platformSettingsApi.testSmtp(testEmail); toast.success(r.data.message); }
    catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
    finally { setSmtpTesting(false); }
  };

  const savePg = async () => {
    setPgSaving(true);
    try {
      await platformSettingsApi.updatePaymentGateways({ razorpay_key_id: pg.razorpay_key_id, razorpay_key_secret: pg.razorpay_key_secret, payu_key: pg.payu_key, payu_salt: pg.payu_salt, payu_mode: pg.payu_mode });
      toast.success('Payment settings saved');
      loadSettings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setPgSaving(false); }
  };

  const testRazorpay = async () => { setRzTesting(true); try { const r = await platformSettingsApi.testRazorpay(); toast.success(r.data.message); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setRzTesting(false); } };
  const testPayu = async () => { setPuTesting(true); try { const r = await platformSettingsApi.testPayu(); toast.success(r.data.message); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setPuTesting(false); } };

  const StatusBadge = ({ configured, envConfigured, label }) => (
    configured ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs gap-1"><CheckCircle className="h-3 w-3" /> {label} (DB)</Badge>
    : envConfigured ? <Badge className="bg-amber-100 text-amber-700 border-0 text-xs gap-1"><ShieldCheck className="h-3 w-3" /> {label} (.env)</Badge>
    : <Badge className="bg-red-100 text-red-700 border-0 text-xs gap-1"><XCircle className="h-3 w-3" /> Not Configured</Badge>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b border-slate-200" data-testid="settings-tabs">
        {[{ key: 'smtp', label: 'Gmail SMTP', icon: Envelope }, { key: 'payment', label: 'Payment Gateways', icon: CreditCard }].map(tab => (
          <button key={tab.key} className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all -mb-[1px] ${activeTab === tab.key ? 'border-sky-500 text-sky-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            onClick={() => setActiveTab(tab.key)} data-testid={`settings-tab-${tab.key}`}><tab.icon className="h-4 w-4" /> {tab.label}</button>
        ))}
      </div>

      {activeTab === 'smtp' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="smtp-settings-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-medium flex items-center gap-2"><Envelope className="h-5 w-5 text-sky-500" /> Platform SMTP</CardTitle>
                  <StatusBadge configured={smtp.configured} envConfigured={smtp.env_configured} label="SMTP" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-500">SMTP Host</Label><Input value={smtp.smtp_host} onChange={e => setSmtp(s => ({...s, smtp_host: e.target.value}))} data-testid="smtp-host-input" /></div>
                  <div><Label className="text-xs text-slate-500">Port</Label><Input type="number" value={smtp.smtp_port} onChange={e => setSmtp(s => ({...s, smtp_port: e.target.value}))} data-testid="smtp-port-input" /></div>
                </div>
                <div><Label className="text-xs text-slate-500">Gmail Address</Label><Input value={smtp.smtp_user} onChange={e => setSmtp(s => ({...s, smtp_user: e.target.value}))} placeholder="your-email@gmail.com" data-testid="smtp-user-input" /></div>
                <div><Label className="text-xs text-slate-500">App Password</Label>
                  <div className="relative"><Input type={showSmtpPass ? 'text' : 'password'} value={smtp.smtp_password} onChange={e => setSmtp(s => ({...s, smtp_password: e.target.value}))} placeholder="xxxx xxxx xxxx xxxx" data-testid="smtp-password-input" />
                    <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowSmtpPass(!showSmtpPass)} type="button">{showSmtpPass ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-500">Sender Email</Label><Input value={smtp.sender_email} onChange={e => setSmtp(s => ({...s, sender_email: e.target.value}))} data-testid="sender-email-input" /></div>
                  <div><Label className="text-xs text-slate-500">Sender Name</Label><Input value={smtp.sender_name} onChange={e => setSmtp(s => ({...s, sender_name: e.target.value}))} data-testid="sender-name-input" /></div>
                </div>
                <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white" onClick={saveSmtp} disabled={smtpSaving} data-testid="save-smtp-btn">{smtpSaving ? 'Saving...' : 'Save SMTP Settings'}</Button>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="test-smtp-card">
              <CardHeader className="pb-3"><CardTitle className="text-base font-medium flex items-center gap-2"><PaperPlaneTilt className="h-5 w-5 text-emerald-500" /> Test Connection</CardTitle></CardHeader>
              <CardContent className="flex gap-2">
                <Input placeholder="test@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} data-testid="test-smtp-email-input" />
                <Button onClick={testSmtp} disabled={smtpTesting} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 whitespace-nowrap" data-testid="send-test-smtp-btn">{smtpTesting ? 'Sending...' : 'Send Test'}</Button>
              </CardContent>
            </Card>
          </div>
          <div className="lg:col-span-2">
            <Card className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 sticky top-6">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-sky-800">Gmail SMTP Setup Guide</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-xs text-sky-900">
                <div><p className="font-semibold mb-1">Step 1: Enable 2-Step Verification</p><p className="text-sky-700">Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="underline font-medium">Google Security</a></p></div>
                <div><p className="font-semibold mb-1">Step 2: Create App Password</p><p className="text-sky-700">Visit <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-medium">App Passwords</a>, select "Mail"</p></div>
                <div><p className="font-semibold mb-1">Step 3: Enter Details</p><p className="text-sky-700">Host: <code className="bg-sky-100 px-1 rounded">smtp.gmail.com</code>, Port: <code className="bg-sky-100 px-1 rounded">587</code></p></div>
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-md"><p className="text-amber-800 font-semibold">Important</p><p className="text-amber-700">Do NOT use your regular Gmail password. Only App Passwords work.</p></div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'payment' && (
        <div className="space-y-6">
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="razorpay-settings-card">
            <CardHeader className="pb-3"><div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2"><Lightning className="h-5 w-5 text-blue-600" /> Razorpay <Badge className="bg-blue-50 text-blue-600 border-0 text-[10px]">Indian Gateway</Badge></CardTitle>
              <StatusBadge configured={pg.razorpay_configured} envConfigured={pg.razorpay_env_configured} label="Razorpay" />
            </div></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-slate-500">Key ID</Label><Input value={pg.razorpay_key_id} onChange={e => setPg(p => ({...p, razorpay_key_id: e.target.value}))} placeholder="rzp_test_xxxxx" data-testid="razorpay-key-input" /></div>
                <div><Label className="text-xs text-slate-500">Key Secret</Label><div className="relative"><Input type={showRzSecret ? 'text' : 'password'} value={pg.razorpay_key_secret} onChange={e => setPg(p => ({...p, razorpay_key_secret: e.target.value}))} data-testid="razorpay-secret-input" />
                  <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowRzSecret(!showRzSecret)} type="button">{showRzSecret ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
              </div>
              <Button variant="outline" size="sm" className="text-blue-600 border-blue-200" onClick={testRazorpay} disabled={rzTesting} data-testid="test-razorpay-btn"><TestTube className="h-4 w-4 mr-1" /> {rzTesting ? 'Testing...' : 'Test Connection'}</Button>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="payu-settings-card">
            <CardHeader className="pb-3"><div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2"><CreditCard className="h-5 w-5 text-green-600" /> PayU <Badge className="bg-green-50 text-green-600 border-0 text-[10px]">Indian Gateway</Badge></CardTitle>
              <StatusBadge configured={pg.payu_configured} envConfigured={pg.payu_env_configured} label="PayU" />
            </div></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div><Label className="text-xs text-slate-500">Merchant Key</Label><Input value={pg.payu_key} onChange={e => setPg(p => ({...p, payu_key: e.target.value}))} data-testid="payu-key-input" /></div>
                <div><Label className="text-xs text-slate-500">Salt</Label><div className="relative"><Input type={showPuSalt ? 'text' : 'password'} value={pg.payu_salt} onChange={e => setPg(p => ({...p, payu_salt: e.target.value}))} data-testid="payu-salt-input" />
                  <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPuSalt(!showPuSalt)} type="button">{showPuSalt ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
                <div><Label className="text-xs text-slate-500">Mode</Label><Select value={pg.payu_mode} onValueChange={v => setPg(p => ({...p, payu_mode: v}))}><SelectTrigger data-testid="payu-mode-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="test">Test</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent></Select></div>
              </div>
              <Button variant="outline" size="sm" className="text-green-600 border-green-200" onClick={testPayu} disabled={puTesting} data-testid="test-payu-btn"><TestTube className="h-4 w-4 mr-1" /> {puTesting ? 'Testing...' : 'Test Connection'}</Button>
            </CardContent>
          </Card>
          <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white" onClick={savePg} disabled={pgSaving} data-testid="save-payment-settings-btn">{pgSaving ? 'Saving...' : 'Save Payment Settings'}</Button>
        </div>
      )}
    </div>
  );
};

// ================= HOSPITAL ADMIN SETTINGS =================
const HospitalSettings = () => {
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(true);
  const [smtp, setSmtp] = useState({ smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_user: '', smtp_password: '', sender_email: '', sender_name: '', configured: false });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [comm, setComm] = useState({ auto_notify_patient: true, enabled_events: [], all_events: [] });
  const [commSaving, setCommSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  // Hospital Info
  const [info, setInfo] = useState({ name: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', website: '', gst_no: '', registration_no: '', logo_url: '' });
  const [infoSaving, setInfoSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const [settingsRes, infoRes] = await Promise.all([
        hospitalSettingsApi.get(),
        hospitalSettingsApi.getInfo(),
      ]);
      setSmtp(s => ({ ...s, ...settingsRes.data.smtp }));
      setComm(settingsRes.data.communication);
      if (infoRes.data) setInfo(prev => ({ ...prev, ...infoRes.data }));
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const saveInfo = async () => {
    setInfoSaving(true);
    try {
      await hospitalSettingsApi.updateInfo(info);
      toast.success('Hospital information saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setInfoSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await hospitalSettingsApi.uploadLogo(fd);
      setInfo(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setLogoUploading(false); }
  };

  const loadLogs = async (page = 1) => {
    setLogsLoading(true);
    try {
      const res = await hospitalSettingsApi.getCommunications({ page, per_page: 15 });
      setLogs(res.data.items);
      setLogsTotal(res.data.total);
      setLogsPage(page);
    } catch { toast.error('Failed to load logs'); }
    finally { setLogsLoading(false); }
  };

  const saveSmtp = async () => {
    if (!smtp.smtp_user) { toast.error('Gmail address is required'); return; }
    setSmtpSaving(true);
    try {
      await hospitalSettingsApi.updateSmtp({ smtp_host: smtp.smtp_host, smtp_port: parseInt(smtp.smtp_port), smtp_user: smtp.smtp_user, smtp_password: smtp.smtp_password, sender_email: smtp.sender_email || smtp.smtp_user, sender_name: smtp.sender_name });
      toast.success('SMTP settings saved');
      loadSettings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSmtpSaving(false); }
  };

  const testSmtp = async () => {
    if (!testEmail) { toast.error('Enter email'); return; }
    setSmtpTesting(true);
    try { const r = await hospitalSettingsApi.testSmtp(testEmail); toast.success(r.data.message); }
    catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
    finally { setSmtpTesting(false); }
  };

  const saveComm = async () => {
    setCommSaving(true);
    try {
      await hospitalSettingsApi.updateCommunication({ auto_notify_patient: comm.auto_notify_patient, enabled_events: comm.enabled_events });
      toast.success('Communication settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCommSaving(false); }
  };

  const toggleEvent = (key) => {
    setComm(c => ({
      ...c,
      enabled_events: c.enabled_events.includes(key) ? c.enabled_events.filter(e => e !== key) : [...c.enabled_events, key]
    }));
  };

  const selectAll = () => setComm(c => ({ ...c, enabled_events: c.all_events.map(e => e.key) }));
  const deselectAll = () => setComm(c => ({ ...c, enabled_events: [] }));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="hospital-settings-tabs">
        {[
          { key: 'info', label: 'Hospital Info', icon: Buildings },
          { key: 'smtp', label: 'Gmail SMTP', icon: Envelope },
          { key: 'communication', label: 'Patient Communication', icon: ChatDots },
          { key: 'logs', label: 'Communication Log', icon: Clock },
        ].map(tab => (
          <button key={tab.key} className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all -mb-[1px] whitespace-nowrap ${activeTab === tab.key ? 'border-sky-500 text-sky-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            onClick={() => { setActiveTab(tab.key); if (tab.key === 'logs') loadLogs(1); }}
            data-testid={`hospital-tab-${tab.key}`}><tab.icon className="h-4 w-4" /> {tab.label}</button>
        ))}
      </div>

      {/* Hospital Info Tab */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle className="text-base font-medium">Hospital Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label>Hospital Name</Label>
                  <Input value={info.name} onChange={e => setInfo(f => ({...f, name: e.target.value}))} placeholder="Hospital name" data-testid="hospital-name-input" /></div>
                <div><Label>Address</Label>
                  <Input value={info.address} onChange={e => setInfo(f => ({...f, address: e.target.value}))} placeholder="Street address" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>City</Label><Input value={info.city} onChange={e => setInfo(f => ({...f, city: e.target.value}))} /></div>
                  <div><Label>State</Label><Input value={info.state} onChange={e => setInfo(f => ({...f, state: e.target.value}))} /></div>
                  <div><Label>Pincode</Label><Input value={info.pincode} onChange={e => setInfo(f => ({...f, pincode: e.target.value}))} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Phone</Label><Input value={info.phone} onChange={e => setInfo(f => ({...f, phone: e.target.value}))} placeholder="+91 XXXXXXXXXX" /></div>
                  <div><Label>Email</Label><Input value={info.email} onChange={e => setInfo(f => ({...f, email: e.target.value}))} placeholder="hospital@example.com" /></div>
                </div>
                <div><Label>Website</Label>
                  <Input value={info.website} onChange={e => setInfo(f => ({...f, website: e.target.value}))} placeholder="https://www.hospital.com" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>GST Number</Label><Input value={info.gst_no} onChange={e => setInfo(f => ({...f, gst_no: e.target.value}))} placeholder="GSTIN" /></div>
                  <div><Label>Registration No</Label><Input value={info.registration_no} onChange={e => setInfo(f => ({...f, registration_no: e.target.value}))} placeholder="Hospital registration number" /></div>
                </div>
                <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-sm gap-2" onClick={saveInfo} disabled={infoSaving} data-testid="save-hospital-info">
                  {infoSaving ? 'Saving...' : 'Save Hospital Information'}
                </Button>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-4">
            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle className="text-base font-medium">Hospital Logo</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {info.logo_url ? (
                  <div className="flex flex-col items-center gap-3">
                    <img src={info.logo_url} alt="Hospital Logo" className="h-24 w-24 object-contain rounded-lg border border-slate-200 bg-white p-2" data-testid="hospital-logo-preview" />
                    <p className="text-[10px] text-slate-400">Current logo</p>
                  </div>
                ) : (
                  <div className="h-24 w-24 mx-auto rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center">
                    <Buildings className="h-8 w-8 text-slate-300" />
                  </div>
                )}
                <div>
                  <Label>Upload Logo (max 2MB)</Label>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={logoUploading}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-sky-50 file:text-sky-600 hover:file:bg-sky-100 mt-1"
                    data-testid="logo-upload-input" />
                  {logoUploading && <p className="text-xs text-sky-600 mt-1 animate-pulse">Uploading...</p>}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-sky-50 border-sky-200">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-semibold text-sky-800 mb-1">Hospital Info is used in:</p>
                <ul className="text-[10px] text-sky-700 space-y-0.5 list-disc list-inside">
                  <li>PDF report headers</li>
                  <li>Patient email communications</li>
                  <li>Invoice and receipt generation</li>
                  <li>Display boards</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* SMTP Tab */}
      {activeTab === 'smtp' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="hospital-smtp-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-medium flex items-center gap-2"><Envelope className="h-5 w-5 text-sky-500" /> Hospital Email Settings</CardTitle>
                  {smtp.configured ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs gap-1"><CheckCircle className="h-3 w-3" /> Configured</Badge>
                    : <Badge className="bg-slate-100 text-slate-500 border-0 text-xs gap-1"><XCircle className="h-3 w-3" /> Not Configured</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-1">Configure your hospital's Gmail SMTP to send automated emails to patients about their claim status updates.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-500">SMTP Host</Label><Input value={smtp.smtp_host} onChange={e => setSmtp(s => ({...s, smtp_host: e.target.value}))} data-testid="h-smtp-host" /></div>
                  <div><Label className="text-xs text-slate-500">Port</Label><Input type="number" value={smtp.smtp_port} onChange={e => setSmtp(s => ({...s, smtp_port: e.target.value}))} data-testid="h-smtp-port" /></div>
                </div>
                <div><Label className="text-xs text-slate-500">Gmail Address</Label><Input value={smtp.smtp_user} onChange={e => setSmtp(s => ({...s, smtp_user: e.target.value}))} placeholder="hospital@gmail.com" data-testid="h-smtp-user" /></div>
                <div><Label className="text-xs text-slate-500">App Password</Label>
                  <div className="relative"><Input type={showPass ? 'text' : 'password'} value={smtp.smtp_password} onChange={e => setSmtp(s => ({...s, smtp_password: e.target.value}))} placeholder="xxxx xxxx xxxx xxxx" data-testid="h-smtp-password" />
                    <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPass(!showPass)} type="button">{showPass ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-500">Sender Email (optional)</Label><Input value={smtp.sender_email} onChange={e => setSmtp(s => ({...s, sender_email: e.target.value}))} placeholder="Same as Gmail" data-testid="h-sender-email" /></div>
                  <div><Label className="text-xs text-slate-500">Sender Name</Label><Input value={smtp.sender_name} onChange={e => setSmtp(s => ({...s, sender_name: e.target.value}))} placeholder="Your Hospital Name" data-testid="h-sender-name" /></div>
                </div>
                <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white" onClick={saveSmtp} disabled={smtpSaving} data-testid="h-save-smtp">{smtpSaving ? 'Saving...' : 'Save SMTP Settings'}</Button>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-base font-medium flex items-center gap-2"><PaperPlaneTilt className="h-5 w-5 text-emerald-500" /> Test Connection</CardTitle></CardHeader>
              <CardContent className="flex gap-2">
                <Input placeholder="test@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} data-testid="h-test-email" />
                <Button onClick={testSmtp} disabled={smtpTesting} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 whitespace-nowrap" data-testid="h-send-test">{smtpTesting ? 'Sending...' : 'Send Test'}</Button>
              </CardContent>
            </Card>
          </div>
          <div className="lg:col-span-2">
            <Card className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 sticky top-6">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-sky-800">Gmail SMTP Setup Guide</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-xs text-sky-900">
                <div><p className="font-semibold mb-1">Step 1: Enable 2-Step Verification</p><p className="text-sky-700">Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="underline font-medium">Google Security Settings</a></p></div>
                <div><p className="font-semibold mb-1">Step 2: Create App Password</p><p className="text-sky-700">Visit <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-medium">App Passwords</a>, select "Mail" and generate a 16-character password.</p></div>
                <div><p className="font-semibold mb-1">Step 3: Enter Details</p><p className="text-sky-700">Host: <code className="bg-sky-100 px-1 rounded">smtp.gmail.com</code>, Port: <code className="bg-sky-100 px-1 rounded">587</code></p></div>
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-md"><p className="text-amber-800 font-semibold">Important</p><p className="text-amber-700">Do NOT use your regular Gmail password. Only App Passwords work with SMTP.</p></div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Communication Settings Tab */}
      {activeTab === 'communication' && (
        <div className="space-y-6">
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="communication-settings-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium flex items-center gap-2"><BellRinging className="h-5 w-5 text-violet-500" /> Patient Auto-Notification</CardTitle>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Auto-notify patients</Label>
                  <Switch checked={comm.auto_notify_patient} onCheckedChange={v => setComm(c => ({...c, auto_notify_patient: v}))} data-testid="auto-notify-toggle" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">When enabled, patients receive automatic emails at each stage of their claim lifecycle. You can choose which events trigger an email.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {comm.auto_notify_patient && (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-700">Notification Events</p>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" className="text-xs text-sky-600" onClick={selectAll}>Select All</Button>
                      <Button variant="ghost" size="sm" className="text-xs text-slate-400" onClick={deselectAll}>Deselect All</Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {(comm.all_events || []).map(ev => (
                      <label key={ev.key}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          comm.enabled_events.includes(ev.key)
                            ? 'border-sky-300 bg-sky-50'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        data-testid={`event-${ev.key}`}
                      >
                        <input type="checkbox" className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          checked={comm.enabled_events.includes(ev.key)} onChange={() => toggleEvent(ev.key)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <div className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color }}></div>
                            <span className="text-sm font-medium text-slate-700 truncate">{ev.label}</span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
              <Button className="w-full bg-gradient-to-r from-violet-500 to-purple-600 text-white" onClick={saveComm} disabled={commSaving} data-testid="save-communication-btn">
                {commSaving ? 'Saving...' : 'Save Communication Settings'}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200">
            <CardContent className="py-4 space-y-2 text-xs text-violet-800">
              <p className="font-semibold">How Patient Communication Works</p>
              <ul className="list-disc pl-4 space-y-1 text-violet-700">
                <li>When a claim's status changes, the patient's registered email receives an automatic notification</li>
                <li>The email includes: Hospital name, Claim number, New status, and a description of what it means</li>
                <li>Emails are sent using your hospital SMTP settings (or platform SMTP as fallback)</li>
                <li>Communication logs are available in the "Communication Log" tab</li>
                <li>The patient must have an email address in their record for notifications to work</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Communication Logs Tab */}
      {activeTab === 'logs' && (
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="communication-logs-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium flex items-center gap-2"><Clock className="h-5 w-5 text-slate-500" /> Patient Communication Log</CardTitle>
            <p className="text-xs text-slate-500">History of automated emails sent to patients</p>
          </CardHeader>
          <CardContent>
            {logsLoading ? (
              <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-500"></div></div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <ChatDots className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm text-slate-500">No patient communications yet</p>
                <p className="text-xs text-slate-400 mt-1">Emails will appear here when claim statuses change</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Patient</TableHead>
                      <TableHead className="text-xs">Claim</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs">Subject</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, i) => (
                      <TableRow key={i} data-testid={`comm-log-${i}`}>
                        <TableCell className="text-xs text-slate-500">{log.created_at ? new Date(log.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</TableCell>
                        <TableCell>
                          <div><p className="text-sm font-medium text-slate-700">{log.patient_name}</p><p className="text-xs text-slate-400">{log.patient_email}</p></div>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-sky-600">{log.claim_number}</TableCell>
                        <TableCell><Badge className="border-0 text-xs bg-sky-50 text-sky-700">{log.status_label}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-600 max-w-[200px] truncate">{log.subject}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {logsTotal > 15 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button variant="outline" size="sm" disabled={logsPage <= 1} onClick={() => loadLogs(logsPage - 1)}>Previous</Button>
                    <span className="text-xs text-slate-500 self-center">Page {logsPage} of {Math.ceil(logsTotal / 15)}</span>
                    <Button variant="outline" size="sm" disabled={logsPage >= Math.ceil(logsTotal / 15)} onClick={() => loadLogs(logsPage + 1)}>Next</Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ================= MAIN SETTINGS PAGE =================
const SettingsPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="space-y-6" data-testid="settings-page">
      <Toaster position="top-right" richColors />
      <div>
        <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="settings-title">
          {isSuperAdmin ? 'Platform Settings' : 'Hospital Settings'}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {isSuperAdmin ? 'Configure SMTP, payment gateways, and platform-wide settings' : 'Configure email, patient communication, and hospital preferences'}
        </p>
      </div>
      {isSuperAdmin ? <SuperAdminSettings /> : <HospitalSettings />}
    </div>
  );
};

export default SettingsPage;
